import { PackageDataApp } from "@granular/fabric3-definitions";

/**
 * This function navigates the file system to find all the app's package.json
 * files, then uses them to return a list of apps with metadata.
 */
export const getAppMetadata = (
  isLocalhost: boolean,
): Array<[string, PackageDataApp]> => {
  /**
   * We use Vite's awesome glob import feature to add apps to the sidebar.
   * For each app found in `apps/` we use the `fabric3:sidebar` key in its
   * `package.json` to get the full name, short name (which is used when the
   * sidebar is collapsed), icon, and the app itself. We do this when the
   * Container mounts.
   *
   * If this alarms you, I encourage you to read this to get an idea of how
   * Vite transforms these dynamic imports 🪄
   *
   * https://vitejs.dev/guide/features.html#glob-import
   *
   * Once we've generated our list, we're ready to draw the sidebar and set up
   * all routes with React Router.
   *
   * NOTE: 🚨 DO NOT ADD ANYTHING HERE BY HAND 🚨
   */

  const appMetadata: Array<[string, PackageDataApp]> =
    /**
     * Convert the object returned by `import.meta.glob` into a list of
     * lists. Easier to manipulate. Please keep reading.
     */
    Object.entries(
      /**
       * This returns an object that has the relative path of the app as the
       * key and the app's package.json data as the value.
       */
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      import.meta.glob("../../../apps/**/package.json", {
        import: "default",
        eager: true,
      }) as Record<string, PackageDataApp>,
    )
      /**
       * Step I: Filter out templates. If you're one of the few Fabric3
       * maintainers, just uncomment this if you need to work on them.
       */
      .filter(
        ([, packageData]) =>
          !packageData.name?.startsWith("@granular/fabric3-template"),
      )
      /**
       * Step II: Filter out unpublished apps. This is just the first pass.
       * We're looking for a key called `publish` under the `fabric3` key in
       * package.json. An 'unpublished' app will not be compiled into the
       * Container via ESBuild. Apps that are 'published' may be in/visible or
       * un/routable. Please keep reading.
       *
       * NOTE: When you develop locally, all apps are always published,
       * visible, and routable!
       */
      .filter(([, packageData]) =>
        !isLocalhost && !packageData.fabric3.publish ? false : true,
      )
      /**
       * Step III: We now replace the keys. We extract the app's namespace from
       * the relative path provided to us by the import meta glob. So:
       *
       *    "../../apps/data/package.json"  👉  "data"
       *
       * Note that we're only looking for lower-kebab-cased apps in the regex.
       * The map function will return a list of the app's namespace and it's
       * package info. All done! ✨
       */
      .map(([relativePath, packageData]) => [
        /..\/..\/apps\/([\w.-]+)\/package.json/.exec(relativePath)![1]!,
        packageData,
      ]);

  return appMetadata;
};
